import { useAuth } from '../contexts/AuthContext';
import { useCallback } from 'react';

type ApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export const useApi = () => {
  const { token, refreshToken, logout } = useAuth();

  // A generic API call function
  const apiFetch = useCallback(
    async (
      url: string,
      method: ApiMethod = 'GET',
      body?: any,
      retry = true,
      responseType: 'json' | 'blob' = 'json'
    ): Promise<any> => {
      const isFormData = body instanceof FormData;
      const headers: Record<string, string> = {};
      if (!isFormData && responseType === 'json') headers['Content-Type'] = `application/json`;
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const options: RequestInit = {
        method,
        headers,
        credentials: 'include', // So the refresh cookie is always sent if needed
      };
      if (body) options.body = isFormData ? body : JSON.stringify(body);


      try {
        const res = await fetch(url, options);

        // Try refresh if 401 (unauthorized)
        if (res.status === 401 && retry) {
          const newToken = await refreshToken();
          if (newToken) {
            // Retry original request with new token
            headers['Authorization'] = `Bearer ${newToken}`;
            const retryRes = await fetch(url, { ...options, headers });
            if (!retryRes.ok) throw retryRes;
            if (responseType === 'blob') {
              return await retryRes.blob();
            }
            return await retryRes.json();
          } else {
            logout();
            throw new Error('Session expired. Please log in again.');
          }
        }

        if (!res.ok) {
          // Try to extract error message
          let errorMessage = 'API Error';
          try {
            const errJson = await res.json();
            errorMessage = errJson.error || errJson.message || errorMessage;
          } catch {}
          throw new Error(errorMessage);
        }

        // All good
        if (responseType === 'blob') {
          return await res.blob();
        }
        return await res.json();
      } catch (error: any) {
        // Could enhance: global error reporting here
        throw error;
      }
    },
    [token, refreshToken, logout]
  );

  return { apiFetch };
};
